import React, { FC, Fragment, useRef } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { Trash2, X } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'
import { ModalBoxOverlay } from 'components/Common/ModalBox/ModalBoxOverlay'

interface IDeleteMeetingConfirmationProps {
  meetingName: string
  onConfirm: () => void
  onCancel: () => void
  isLoading?: boolean
}

const DeleteMeetingConfirmation: FC<IDeleteMeetingConfirmationProps> = ({
  meetingName,
  onConfirm,
  onCancel,
  isLoading = false,
}) => {
  const { t } = useTranslation()
  const initialRef = useRef(null)

  return (
    <Transition appear show={true} as={Fragment}>
      <Dialog
        initialFocus={initialRef}
        as="div"
        className="relative z-[60]"
        onClose={() => undefined}
      >
        <ModalBoxOverlay bgColor="bg-black/50 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel
              ref={initialRef}
              className="bg-white border border-grey-lighter rounded-2xl shadow-xl w-full max-w-[492px] p-6 flex flex-col gap-8 items-center"
            >
              {/* Top row: close X with vertical divider on its left */}
              <div className="flex items-center justify-end w-full">
                <div className="flex items-center gap-2">
                  <div className="bg-grey-lighter h-6 w-px" />
                  <button
                    type="button"
                    onClick={onCancel}
                    className="p-1.5 rounded-lg hover:bg-grey-light text-primary-text"
                    aria-label={t('calendar.viewMeeting.close')}
                    disabled={isLoading}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Body: pink trash icon, title, message */}
              <div className="flex flex-col gap-6 items-center w-full">
                <div className="bg-red-light rounded-lg p-2">
                  <Trash2 className="w-8 h-8 text-red" strokeWidth={1.5} />
                </div>
                <div className="flex flex-col gap-2 items-center text-center w-full">
                  <p className="text-base font-medium text-black-appDark leading-6">
                    {t('calendar.deleteMeeting.title')}
                  </p>
                  <p className="text-sm text-black-appDime leading-5">
                    <Trans
                      i18nKey="calendar.deleteMeeting.confirm"
                      values={{ name: meetingName }}
                      components={{ 1: <span className="font-bold" /> }}
                    />
                  </p>
                </div>
              </div>

              {/* CAL-18: an irreversible action needs a visible way out, not just
                  the ✕ in the corner. */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={isLoading}
                  className="flex-1 border border-primary-outline text-primary-text hover:text-primary text-sm font-medium leading-5 rounded-lg px-4 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('calendar.modal.cancel')}
                </button>
                <button
                  type="button"
                  onClick={onConfirm}
                  disabled={isLoading}
                  className="flex-1 bg-red hover:opacity-90 text-white text-sm font-medium leading-5 rounded-lg px-4 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading
                    ? t('calendar.deleteMeeting.deleting')
                    : t('calendar.deleteMeeting.delete')}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  )
}

export default DeleteMeetingConfirmation
